import React, { useState, useMemo } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import ReportsBackIcon from '../../../../assets/icons/reports-back-icon';
import { DashboardNavState } from './types';
import { Level1Groups } from './level-1-groups';
import { Level2Teams } from './level-2-teams';
import { Level3Users } from './level-3-users';

interface ContextType {
    setReportTitle: (title: string) => void;
    setReportDescText: (desc: string) => void;
}

export const GroupsTeamsActivityDashboard: React.FC = () => {
    const { setReportTitle, setReportDescText } = useOutletContext<ContextType>();
    const navigate = useNavigate();

    const [navState, setNavState] = useState<DashboardNavState>({
        level: 1,
        groupId: null,
        groupName: null,
        teamId: null,
        teamName: null,
    });

    useMemo(() => {
        setReportTitle('');
        setReportDescText('');
    }, []);

    const handleNavigate = (partial: Partial<DashboardNavState>) => {
        setNavState((prev) => ({ ...prev, ...partial }));
        window.scrollTo(0, 0);
    };

    const handleBack = () => {
        if (navState.level === 3) {
            handleNavigate({ level: 2, teamId: null, teamName: null });
        } else if (navState.level === 2) {
            handleNavigate({ level: 1, groupId: null, groupName: null, teamId: null, teamName: null });
        } else {
            navigate('/reports');
        }
    };

    return (
        <Stack>
            {/* Back Button */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'left',
                    textAlign: 'left',
                    marginTop: 4,
                    marginBottom: 3,
                }}
            >
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleBack}
                    sx={{
                        border: 0,
                        padding: 1,
                        boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.24),0 16px 32px -4px rgba(145, 158, 171, 0.24)',
                    }}
                >
                    <ReportsBackIcon />
                    <Typography fontWeight={400} fontSize={14} color="black" marginLeft={1}>
                        Back
                    </Typography>
                </Button>
            </Box>

            {/* Level Content */}
            <Box sx={{ fontFamily: 'Poppins, sans-serif' }}>
                {navState.level === 1 && (
                    <Level1Groups onNavigate={handleNavigate} />
                )}
                {navState.level === 2 && navState.groupId && navState.groupName && (
                    <Level2Teams
                        groupId={navState.groupId}
                        groupName={navState.groupName}
                        onNavigate={handleNavigate}
                    />
                )}
                {navState.level === 3 && navState.groupId && navState.groupName && navState.teamId && navState.teamName && (
                    <Level3Users
                        groupId={navState.groupId}
                        groupName={navState.groupName}
                        teamId={navState.teamId}
                        teamName={navState.teamName}
                        onNavigate={handleNavigate}
                    />
                )}
            </Box>
        </Stack>
    );
};
